import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import Avatar from "../components/Avatar";
import useDarkMode from "../hooks/useDarkMode";
import useTimedMessage from "../hooks/useTimedMessage";
import { getApiErrorMessage } from "../lib/apiErrors";
import { toast } from "../lib/toast";

const Settings = () => {
  const { currentUser, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [isDark, toggleDark] = useDarkMode();

  // Active section for sidebar navigation
  const [activeSection, setActiveSection] = useState("account");

  // Account form state
  const [accountForm, setAccountForm] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    diet_preference: "",
    bio: "",
    location: "",
  });
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountMsg, setAccountMsg] = useTimedMessage(3000);
  const avatarInputRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    old_password: "",
    new_password1: "",
    new_password2: "",
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState({ text: "", type: "" });
  const passwordTimerRef = useRef(null);

  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState({
    follow: true,
    like: true,
    comment: true,
    fork: true,
    recipe_share: true,
  });
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifMsg, setNotifMsg] = useTimedMessage(3000);

  // Privacy state
  const [defaultVisibility, setDefaultVisibility] = useState("public");
  const [privacySaving, setPrivacySaving] = useState(false);
  const [privacyMsg, setPrivacyMsg] = useTimedMessage(3000);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    return () => { if (passwordTimerRef.current) clearTimeout(passwordTimerRef.current); };
  }, []);

  // Load current user data into forms on mount
  useEffect(() => {
    if (currentUser) {
      setAccountForm({
        first_name: currentUser.first_name || "",
        last_name: currentUser.last_name || "",
        phone_number: currentUser.phone_number || "",
        diet_preference: currentUser.diet_preference || "",
        bio: currentUser.bio || "",
        location: currentUser.location || "",
      });
      setDefaultVisibility(currentUser.default_visibility || "public");
      setNotifPrefs({
        follow: currentUser.notification_preferences?.follow ?? true,
        like: currentUser.notification_preferences?.like ?? true,
        comment: currentUser.notification_preferences?.comment ?? true,
        fork: currentUser.notification_preferences?.fork ?? true,
        recipe_share: currentUser.notification_preferences?.recipe_share ?? true,
      });
    }
  }, [currentUser]);

  // === Account Section ===
  const handleAccountChange = (e) => {
    setAccountForm({ ...accountForm, [e.target.name]: e.target.value });
  };

  const handleAccountSave = async () => {
    setAccountSaving(true);
    setAccountMsg("");
    try {
      await axiosInstance.patch("/api/user/profile/", accountForm);
      if (refreshUser) await refreshUser();
      setAccountMsg("Profile updated successfully.");

    } catch (err) {
      setAccountMsg("Failed to update profile.");
    } finally {
      setAccountSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const data = new FormData();
      data.append("profile_picture", file);
      await axiosInstance.patch("/api/user/profile/", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (refreshUser) await refreshUser();
      toast.success("Profile picture updated.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to upload profile picture."));
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  // === Password Section ===
  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
  };

  const handlePasswordSave = async () => {
    if (passwordForm.new_password1 !== passwordForm.new_password2) {
      setPasswordMsg({ text: "New passwords do not match.", type: "error" });
      return;
    }
    if (passwordForm.new_password1.length < 8) {
      setPasswordMsg({ text: "Password must be at least 8 characters.", type: "error" });
      return;
    }
    setPasswordSaving(true);
    setPasswordMsg({ text: "", type: "" });
    try {
      await axiosInstance.post("/api/auth/password/change/", passwordForm);
      setPasswordMsg({ text: "Password changed successfully.", type: "success" });
      setPasswordForm({ old_password: "", new_password1: "", new_password2: "" });
      if (passwordTimerRef.current) clearTimeout(passwordTimerRef.current);
      passwordTimerRef.current = setTimeout(() => setPasswordMsg({ text: "", type: "" }), 3000);
    } catch (err) {
      const detail = err.response?.data?.old_password?.[0]
        || err.response?.data?.new_password2?.[0]
        || "Failed to change password.";
      setPasswordMsg({ text: detail, type: "error" });
    } finally {
      setPasswordSaving(false);
    }
  };

  // === Notifications Section ===
  const handleNotifToggle = (key) => {
    setNotifPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleNotifSave = async () => {
    setNotifSaving(true);
    setNotifMsg("");
    try {
      await axiosInstance.patch("/api/user/profile/", {
        notification_preferences: notifPrefs,
      });
      if (refreshUser) await refreshUser();
      setNotifMsg("Notification preferences saved.");

    } catch (err) {
      setNotifMsg("Failed to save preferences.");
    } finally {
      setNotifSaving(false);
    }
  };

  // === Privacy Section ===
  const handlePrivacySave = async () => {
    setPrivacySaving(true);
    setPrivacyMsg("");
    try {
      await axiosInstance.patch("/api/user/profile/", {
        default_visibility: defaultVisibility,
      });
      if (refreshUser) await refreshUser();
      setPrivacyMsg("Privacy settings saved.");

    } catch (err) {
      setPrivacyMsg("Failed to save privacy settings.");
    } finally {
      setPrivacySaving(false);
    }
  };

  // === Delete Account ===
  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError("Please enter your password.");
      return;
    }
    setDeleting(true);
    setDeleteError("");
    try {
      await axiosInstance.delete("/api/user/delete/", {
        data: { password: deletePassword },
      });
      logout();
      navigate("/login");
    } catch (err) {
      setDeleteError(err.response?.data?.detail || "Failed to delete account.");
    } finally {
      setDeleting(false);
    }
  };

  // Toggle switch component
  const Toggle = ({ enabled, onChange }) => (
    <button
      type="button"
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
        enabled ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white transition transform ${
          enabled ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );

  const sections = [
    { key: "account", label: "Account" },
    { key: "security", label: "Security" },
    { key: "notifications", label: "Notifications" },
    { key: "privacy", label: "Privacy" },
    { key: "appearance", label: "Appearance" },
    { key: "danger", label: "Danger Zone" },
  ];

  return (
    <div className="flex-1 p-6 max-w-4xl mx-auto">
      <h1 className="type-page-title mb-6">Settings</h1>

      <div className="flex gap-8">
        {/* Sidebar nav */}
        <nav className="w-48 flex-shrink-0">
          <div className="space-y-1">
            {sections.map((s) => (
              <button
                key={s.key}
                onClick={() => setActiveSection(s.key)}
                className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition ${
                  activeSection === s.key
                    ? "bg-blue-50 text-blue-600 dark:bg-blue-900/30"
                    : s.key === "danger"
                    ? "text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Content area */}
        <div className="flex-1 min-w-0">
          {/* Account Section */}
          {activeSection === "account" && (
            <div className="space-y-6">
              <h2 className="type-section-title">Account</h2>

              {/* Profile picture */}
              <div className="flex items-center space-x-4">
                <div
                  className="relative cursor-pointer group"
                  onClick={() => avatarInputRef.current?.click()}
                >
                  <Avatar user={currentUser} size="lg" />
                  <div className="absolute inset-0 w-16 h-16 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                    <span className="text-white text-xs font-medium">
                      {uploadingAvatar ? "..." : "Edit"}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="type-card-title">Profile Photo</p>
                  <p className="type-muted text-xs">Click avatar to change</p>
                </div>
                <input
                  type="file"
                  ref={avatarInputRef}
                  onChange={handleAvatarUpload}
                  accept="image/*"
                  className="hidden"
                />
              </div>

              {/* Form fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    value={accountForm.first_name}
                    onChange={handleAccountChange}
                    className="border rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    value={accountForm.last_name}
                    onChange={handleAccountChange}
                    className="border rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Email</label>
                <input
                  type="text"
                  value={currentUser?.email || ""}
                  disabled
                  className="border rounded-lg px-3 py-2 text-sm w-full bg-gray-50 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Phone Number</label>
                  <input
                    type="text"
                    name="phone_number"
                    value={accountForm.phone_number}
                    onChange={handleAccountChange}
                    placeholder="e.g., (555) 123-4567"
                    className="border rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Location</label>
                  <input
                    type="text"
                    name="location"
                    value={accountForm.location}
                    onChange={handleAccountChange}
                    placeholder="e.g., San Jose, CA"
                    className="border rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Diet Preference</label>
                <input
                  type="text"
                  name="diet_preference"
                  value={accountForm.diet_preference}
                  onChange={handleAccountChange}
                  placeholder="e.g., Vegan, Keto, Meat"
                  className="border rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Bio</label>
                <textarea
                  name="bio"
                  value={accountForm.bio}
                  onChange={handleAccountChange}
                  rows={3}
                  maxLength={500}
                  placeholder="Tell people about yourself..."
                  className="border rounded-lg px-3 py-2 text-sm w-full resize-none focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={handleAccountSave}
                  disabled={accountSaving}
                  className="px-5 py-2 rounded-lg text-sm font-semibold bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 transition"
                >
                  {accountSaving ? "Saving..." : "Save Changes"}
                </button>
                {accountMsg && (
                  <span className="text-sm text-green-600">{accountMsg}</span>
                )}
              </div>
            </div>
          )}

          {/* Security Section */}
          {activeSection === "security" && (
            <div className="space-y-6">
              <h2 className="type-section-title">Change Password</h2>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Current Password</label>
                <input
                  type="password"
                  name="old_password"
                  value={passwordForm.old_password}
                  onChange={handlePasswordChange}
                  autoComplete="off"
                  className="border rounded-lg px-3 py-2 text-sm w-full max-w-md focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">New Password</label>
                <input
                  type="password"
                  name="new_password1"
                  value={passwordForm.new_password1}
                  onChange={handlePasswordChange}
                  autoComplete="new-password"
                  className="border rounded-lg px-3 py-2 text-sm w-full max-w-md focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  name="new_password2"
                  value={passwordForm.new_password2}
                  onChange={handlePasswordChange}
                  autoComplete="new-password"
                  className="border rounded-lg px-3 py-2 text-sm w-full max-w-md focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={handlePasswordSave}
                  disabled={passwordSaving}
                  className="px-5 py-2 rounded-lg text-sm font-semibold bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 transition"
                >
                  {passwordSaving ? "Changing..." : "Change Password"}
                </button>
                {passwordMsg.text && (
                  <span className={`text-sm ${passwordMsg.type === "error" ? "text-red-500" : "text-green-600"}`}>
                    {passwordMsg.text}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Notifications Section */}
          {activeSection === "notifications" && (
            <div className="space-y-6">
              <h2 className="type-section-title">Notification Preferences</h2>
              <p className="type-muted">Choose which notifications you'd like to receive.</p>

              <div className="space-y-4">
                {[
                  { key: "follow", label: "New followers", desc: "When someone follows you" },
                  { key: "like", label: "Likes", desc: "When someone likes your recipe or post" },
                  { key: "comment", label: "Comments", desc: "When someone comments on your recipe or post" },
                  { key: "fork", label: "Forks", desc: "When someone forks your recipe" },
                  { key: "recipe_share", label: "Recipe shares", desc: "When someone you follow shares a recipe" },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between py-3 border-b dark:border-gray-700 last:border-b-0">
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
                      <p className="text-xs text-gray-400">{desc}</p>
                    </div>
                    <Toggle enabled={notifPrefs[key]} onChange={() => handleNotifToggle(key)} />
                  </div>
                ))}
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={handleNotifSave}
                  disabled={notifSaving}
                  className="px-5 py-2 rounded-lg text-sm font-semibold bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 transition"
                >
                  {notifSaving ? "Saving..." : "Save Preferences"}
                </button>
                {notifMsg && (
                  <span className="text-sm text-green-600">{notifMsg}</span>
                )}
              </div>
            </div>
          )}

          {/* Privacy Section */}
          {activeSection === "privacy" && (
            <div className="space-y-6">
              <h2 className="type-section-title">Privacy</h2>

              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Default Recipe Visibility</label>
                  <p className="text-xs text-gray-400 mb-2">New recipes will default to this visibility setting.</p>
                  <select
                    value={defaultVisibility}
                    onChange={(e) => setDefaultVisibility(e.target.value)}
                    className="border rounded-lg px-3 py-2 text-sm w-full max-w-xs focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                  </select>
                </div>

              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={handlePrivacySave}
                  disabled={privacySaving}
                  className="px-5 py-2 rounded-lg text-sm font-semibold bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 transition"
                >
                  {privacySaving ? "Saving..." : "Save Privacy Settings"}
                </button>
                {privacyMsg && (
                  <span className="text-sm text-green-600">{privacyMsg}</span>
                )}
              </div>
            </div>
          )}

          {/* Appearance Section */}
          {activeSection === "appearance" && (
            <div className="space-y-6">
              <h2 className="type-section-title">Appearance</h2>

              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Dark Mode</p>
                  <p className="text-xs text-gray-400">Switch between light and dark theme. Synced across all your devices.</p>
                </div>
                <Toggle enabled={isDark} onChange={toggleDark} />
              </div>
            </div>
          )}

          {/* Danger Zone Section */}
          {activeSection === "danger" && (
            <div className="space-y-6">
              <h2 className="type-section-title text-red-600 dark:text-red-500">Danger Zone</h2>

              <div className="border border-red-200 rounded-lg p-5 bg-red-50 dark:border-red-800 dark:bg-red-900/30">
                <h3 className="type-card-title text-red-700 dark:text-red-400">Delete Account</h3>
                <p className="text-xs text-red-500 mt-1">
                  Permanently delete your account and all associated data including recipes, comments, likes, and followers. This action cannot be undone.
                </p>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="mt-4 px-5 py-2 rounded-lg text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition"
                >
                  Delete My Account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md p-6">
            <h2 className="type-section-title text-red-600 dark:text-red-500 mb-2">Delete Account</h2>
            <p className="type-muted mb-4">
              This will permanently delete your account and all your data. Enter your password to confirm.
            </p>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Enter your password"
              className="border rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-red-400 mb-3 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
            {deleteError && (
              <p className="text-sm text-red-500 mb-3">{deleteError}</p>
            )}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletePassword("");
                  setDeleteError("");
                }}
                className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition"
              >
                {deleting ? "Deleting..." : "Delete Forever"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
